import * as React from 'react';
import {Tooltip, Typography, withStyles} from '@material-ui/core';
import {withTranslation} from 'react-i18next';
import {compose, Dispatch} from 'redux';
import {withDataProvider} from '../../fetchApi/dataProvider';
import hasPermissions from "../../functions/permissions/hasPermissions";
import {GET} from '../../fetchApi/types';
import {
    dataIsValidAfterDelete,
    dataIsValidAfterSave,
    dataValidityKey,
    endpointPlanInfo,
    realtimeDataReducerKey,
    resourceCustomUsers,
    resourcePlan,
    resourcePlanHistories,
    resourceRecognitionBonuses,
    resourceRequests, roleAdmin, roleOperator,
} from '../../constants';
import {connect} from 'react-redux';
import {changeAfterSaveDataValidity} from '../../actions/RealtimeActions';
import moment from 'moment';
import _ from 'lodash';
import {TFunction} from 'i18next';
import {DataProvider} from '../../fetchApi/tsTypes';
import {PlanInfo} from "../../typings/models";
import {replaceData} from "../../actions/DataActions";

interface InternalCounterProps {
    classes: Record<string, string>;
    changeDataAction: (params: { resource: string, payload: any }) => void;
    dataIsValid: boolean;
    t: TFunction;
    dispatch: Dispatch;
    dataProvider: DataProvider;
}

interface CounterComponentState {
    planInfo: PlanInfo | null;
}

const infinitySign = '\u221e';

class CounterComponent extends React.Component<InternalCounterProps, CounterComponentState> {
    private readonly isAdmin: boolean;
    private readonly isOperator: boolean;

    constructor(props: InternalCounterProps) {
        super(props);
        this.state = {
            planInfo: null,
        };
        this.isAdmin = hasPermissions(roleAdmin);
        this.isOperator = hasPermissions(roleOperator);
    }

    fetchData = () => {
        if (this.isAdmin || this.isOperator) {
            return;
        }

        const {dataProvider, dispatch, changeDataAction} = this.props;

        dataProvider(GET, `${resourceCustomUsers}/${endpointPlanInfo}`, {})
            .then(data => {
                const localPlanInfo: PlanInfo = {...data.data};

                let allowedPagesNumber = localPlanInfo.allowedLimit;
                let allowedPages = infinitySign;
                if (allowedPagesNumber) {
                    allowedPagesNumber += localPlanInfo.bonusLimit ? localPlanInfo.bonusLimit : 0;
                    allowedPages = String(allowedPagesNumber);
                }

                localPlanInfo.allowedLimitString = `${allowedPages}`;

                localPlanInfo.formattedStartTime = moment(localPlanInfo.startTime).format('DD.MM.YYYY');
                localPlanInfo.formattedEndTime = moment(localPlanInfo.endTime).format('DD.MM.YYYY');
                localPlanInfo.localEndTime = moment(localPlanInfo.endTime).local().format('DD.MM.YYYY HH:mm:ss');

                this.setState({planInfo: localPlanInfo});
                dispatch(replaceData({keys: resourcePlan, value: localPlanInfo}));
            })
            .catch(e => {
                console.error(e);
            })
            .finally(() => {
                changeDataAction({resource: resourceRequests, payload: []});
                changeDataAction({resource: resourcePlanHistories, payload: []});
                changeDataAction({resource: resourceRecognitionBonuses, payload: []});
            });
    };

    componentDidMount(): void {
        this.fetchData();
    }

    componentDidUpdate(prevProps: Readonly<InternalCounterProps>): void {
        const {dataIsValid: prevDataIsValid} = prevProps;
        const {dataIsValid} = this.props;

        if (prevDataIsValid && !dataIsValid) {
            this.fetchData();
        }
    }

    render(): React.ReactElement {
        const {t: translate, classes} = this.props;
        const {planInfo} = this.state;

        if (planInfo === null) {
            return (
                <div/>
            );
        } else {
            const tooltipTitleMessage = (this.isAdmin || this.isOperator) ? translate('documents.admin_counter_message') :
                translate('documents.counter_message',
                    {
                        to: planInfo.formattedEndTime,
                        allowedLimit: planInfo.allowedLimit ? planInfo.allowedLimit : infinitySign,
                        bonusLimit: planInfo.bonusLimit,
                        dataExtractionLimit: planInfo.totalLimitDataExtraction,
                        recognizedPages: planInfo.recognizedPages,
                        dataExtractionUsed: planInfo.recognizedDataExtractionPages,
                        name: planInfo.name,
                    },
                );
            const text = (this.isAdmin || this.isOperator) ?
                `Limit: 0 / ${infinitySign}` : `Limit: ${planInfo.recognizedPages} / ${planInfo.allowedLimitString}`;
            const dateText = (this.isAdmin || this.isOperator) ? null : translate('documents.renew_date_message',
                {
                    renewDate: planInfo.localEndTime,
                },
            );

            return (
                <Tooltip
                    title={
                        <div style={{whiteSpace: 'pre-line'}}>
                            <Typography className={classes.tooltipTitle}>
                                {tooltipTitleMessage}
                            </Typography>
                        </div>
                    }
                >
                    <div>
                        <Typography className={classes.counter}>
                            {text}
                        </Typography>
                        <Typography className={classes.footnote}>
                            {dateText}
                        </Typography>
                    </div>
                </Tooltip>
            );
        }
    }
}

const mapStateToProps = (state: Record<string, unknown>): { dataIsValid: boolean } => {
    const requestsValidity = _.get(state, [realtimeDataReducerKey, resourceRequests, dataValidityKey], null);
    const planHistoriesValidity = _.get(state, [realtimeDataReducerKey, resourcePlanHistories, dataValidityKey], null);
    const bonusesValidity = _.get(state, [realtimeDataReducerKey, resourceRecognitionBonuses, dataValidityKey], null);

    let dataIsValid = true;
    if (requestsValidity) {
        dataIsValid = dataIsValid && requestsValidity[dataIsValidAfterSave];
    }
    if (planHistoriesValidity) {
        dataIsValid = dataIsValid && planHistoriesValidity[dataIsValidAfterSave];
    }
    if (bonusesValidity) {
        dataIsValid = dataIsValid && bonusesValidity[dataIsValidAfterSave] && bonusesValidity[dataIsValidAfterDelete];
    }

    return ({
        dataIsValid,
    });
};

const RecognizedPagesCounter: React.FunctionComponent<Record<string, unknown>> = compose(
    connect(mapStateToProps, {
        changeDataAction: changeAfterSaveDataValidity,
    }),
    withTranslation(),
    withDataProvider,
    withStyles({
        counter: {
            color: '#464D69',
            fontSize: 18,
        },
        tooltipTitle: {
            fontSize: 16,
        },
        footnote: {
            color: '#464D69',
            fontSize: 9,
        },
    }),
)(CounterComponent);

export default RecognizedPagesCounter;